import React, { useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ExternalLink, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdCard = ({ ad, variant = 'default', className = '' }) => {
  const isSystemPromotion = ad.ad_type === 'system_promotion';

  // Track impression when ad is rendered
  useEffect(() => {
    if (ad.id) {
      trackImpression(ad.id);
    }
  }, [ad.id]);

  const trackImpression = async (adId) => {
    try {
      await fetch(`${API_URL}/api/ads/public/ads/${adId}/impression`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Failed to track impression', error);
    }
  };

  const handleClick = async () => {
    // Track click
    try {
      await fetch(`${API_URL}/api/ads/public/ads/${ad.id}/click`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Failed to track click', error);
    }

    // Open link
    if (ad.destination_link) {
      window.open(ad.destination_link, '_blank', 'noopener,noreferrer');
    }
  };

  // Compact variant (for marketplace/products)
  if (variant === 'compact') {
    return (
      <Card className={`hover:shadow-lg transition-shadow cursor-pointer border-2 ${isSystemPromotion ? 'border-purple-200' : 'border-blue-200'} ${className}`} onClick={handleClick}>
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            {/* Image */}
            {ad.image_url && (
              <img
                src={ad.image_url}
                alt={ad.headline}
                className="w-24 h-24 object-cover rounded flex-shrink-0"
              />
            )}
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-bold text-lg line-clamp-1">{ad.headline}</h3>
                <Badge variant={isSystemPromotion ? "secondary" : "default"} className="flex-shrink-0">
                  {isSystemPromotion ? (
                    <><Sparkles className="h-3 w-3 mr-1" />Promoted</>
                  ) : (
                    <>Sponsored</>
                  )}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                {ad.description}
              </p>
              <div className="flex items-center justify-between">
                {ad.business_name && (
                  <span className="text-xs text-muted-foreground">{ad.business_name}</span>
                )}
                <Button size="sm" variant="outline">
                  {ad.cta_text || 'Learn More'}
                  <ExternalLink className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Default variant (for discover/feeds)
  return (
    <Card className={`hover:shadow-lg transition-shadow cursor-pointer ${isSystemPromotion ? 'border-purple-200' : 'border-blue-200'} ${className}`} onClick={handleClick}>
      <CardContent className="p-0">
        {/* Image */}
        {ad.image_url && (
          <div className="relative">
            <img
              src={ad.image_url}
              alt={ad.headline}
              className="w-full h-48 object-cover rounded-t"
            />
            <Badge 
              variant={isSystemPromotion ? "secondary" : "default"} 
              className="absolute top-3 right-3"
            >
              {isSystemPromotion ? (
                <><Sparkles className="h-3 w-3 mr-1" />Promoted</>
              ) : (
                <>Sponsored</>
              )}
            </Badge>
          </div>
        )}
        
        {/* Content */}
        <div className="p-6">
          {!ad.image_url && (
            <Badge 
              variant={isSystemPromotion ? "secondary" : "default"} 
              className="mb-3"
            >
              {isSystemPromotion ? (
                <><Sparkles className="h-3 w-3 mr-1" />Promoted</>
              ) : (
                <>Sponsored</>
              )}
            </Badge>
          )}
          
          {ad.business_name && !isSystemPromotion && (
            <div className="text-xs text-muted-foreground mb-2">{ad.business_name}</div>
          )}
          
          <h3 className="text-xl font-bold mb-2">{ad.headline}</h3>
          <p className="text-muted-foreground mb-4 line-clamp-3">
            {ad.description}
          </p>
          
          <Button className="w-full">
            {ad.cta_text || 'Learn More'}
            <ExternalLink className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Hook to fetch ads for a placement
export const useAds = (placement) => {
  const [ads, setAds] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (placement) {
      fetchAds();
    }
  }, [placement]);

  const fetchAds = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ads/public/ads/placement/${placement}`);
      if (response.ok) {
        const data = await response.json();
        setAds(data.ads || []);
      }
    } catch (error) {
      console.error('Failed to fetch ads', error);
    } finally {
      setLoading(false);
    }
  };

  return { ads, loading, refetch: fetchAds };
};
